import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  CheckCircle as CompletedIcon,
  LocalShipping as InProgressIcon,
  Assignment as AssignedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelledIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useTaskLocationHistory } from '../hooks/useTaskLocationHistory';

interface TaskLocationHistoryProps {
  taskId: string;
}

const TaskLocationHistory: React.FC<TaskLocationHistoryProps> = ({ taskId }) => {
  const { history: locationHistory, loading, error } = useTaskLocationHistory(taskId);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon />;
      case 'in_progress':
        return <InProgressIcon />;
      case 'assigned':
        return <AssignedIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'cancelled':
        return <CancelledIcon />;
      default:
        return <LocationIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'primary' | 'warning' | 'grey' | 'error' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'assigned':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'grey';
    }
  };

  const getChipStatusColor = (status: string): 'success' | 'primary' | 'warning' | 'default' | 'error' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'assigned':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const openInGoogleMaps = (lat: number, lng: number) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (locationHistory.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        No location history available for this task yet.
      </Alert>
    );
  }

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          📍 Task Location History
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          GPS tracking for each status change
        </Typography>

        <Timeline position="alternate">
          {locationHistory.map((entry, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent>
                <Typography variant="body2" color="text.secondary">
                  {formatTimestamp(entry.timestamp)}
                </Typography>
                {entry.ninjaId && (
                  <Typography variant="caption" color="text.secondary">
                    Ninja: {entry.ninjaId.substring(0, 8)}...
                  </Typography>
                )}
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot color={getStatusColor(entry.status)}>
                  {getStatusIcon(entry.status)}
                </TimelineDot>
                {index < locationHistory.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip
                        label={entry.status.toUpperCase().replace('_', ' ')}
                        color={getChipStatusColor(entry.status)}
                        size="small"
                      />
                    </Box>

                    {entry.location && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Latitude:</strong> {entry.location.latitude.toFixed(6)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Longitude:</strong> {entry.location.longitude.toFixed(6)}
                        </Typography>
                        <Box mt={1}>
                          <Chip
                            icon={<LocationIcon />}
                            label="View on Map"
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() =>
                              openInGoogleMaps(entry.location.latitude, entry.location.longitude)
                            }
                            sx={{ cursor: 'pointer' }}
                          />
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </CardContent>
    </Card>
  );
};

export default TaskLocationHistory;
